import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
  Badge,
  Button,
  FormField,
  Textarea,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  type InterviewKit,
  type Scorecard,
  useSubmitScorecard,
} from '../../hooks/use-interview-kits'
import {
  SCORECARD_RECOMMENDATIONS,
  initialScorecardDraft,
  scorecardDraftError,
  scorecardPayload,
  type ScorecardDraft,
  type ScorecardDraftRating,
} from '../../lib/scorecard'

export type ScorecardCanvasFormProps = {
  applicationId: string
  candidateName: string
  kit: InterviewKit
  interviewId?: string | null
  previous?: Scorecard | null
  onClose: () => void
}

/** Interviewer rates each kit competency against its target level, then recommends. */
export function ScorecardCanvasForm({
  applicationId,
  candidateName,
  kit,
  interviewId,
  previous,
  onClose,
}: ScorecardCanvasFormProps) {
  const submit = useSubmitScorecard()
  const [draft, setDraft] = useState<ScorecardDraft>(() =>
    initialScorecardDraft(kit.topics, previous),
  )
  const [error, setError] = useState<string | null>(null)

  const setRating = (index: number, patch: Partial<ScorecardDraftRating>) =>
    setDraft((d) => ({
      ...d,
      ratings: d.ratings.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }))

  const onSave = () => {
    const problem = scorecardDraftError(draft)
    if (problem) {
      setError(problem)
      return
    }
    setError(null)
    submit.mutate(
      { applicationId, data: scorecardPayload(draft, interviewId) },
      {
        onSuccess: (card) => {
          toast.success(`Scorecard sent: ${card.recommendation_label}`)
          onClose()
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Could not submit the scorecard.'),
      },
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        <p className="text-sm text-muted-foreground">
          Rate {candidateName} on each {kit.role_title ? 'competency' : 'skill'}. The recruiter
          sees your recommendation on their board.
        </p>

        {draft.ratings.map((rating, index) => {
          const topic = kit.topics[index]
          return (
            <fieldset key={rating.competency_id ?? rating.topic} className="space-y-2">
              <legend className="flex items-center gap-2 text-sm font-medium">
                {rating.topic}
                {rating.target_level != null ? (
                  <span className="text-xs font-normal text-muted-foreground">
                    target {rating.target_level}
                  </span>
                ) : null}
              </legend>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${rating.topic} level`}>
                {topic.levels.map((level) => {
                  const selected = rating.level === level.ordinal
                  return (
                    <Button
                      key={level.ordinal}
                      type="button"
                      size="sm"
                      variant={selected ? 'default' : 'outline'}
                      aria-pressed={selected}
                      title={level.anchor || undefined}
                      onClick={() => setRating(index, { level: level.ordinal })}
                    >
                      {level.ordinal} {level.label}
                      {rating.target_level === level.ordinal ? (
                        <Badge variant="secondary" className="ml-1 px-1 py-0 text-[10px]">
                          target
                        </Badge>
                      ) : null}
                    </Button>
                  )
                })}
              </div>
              <Textarea
                aria-label={`${rating.topic} notes`}
                placeholder="Evidence (optional)"
                rows={2}
                value={rating.notes}
                onChange={(e) => setRating(index, { notes: e.target.value })}
              />
            </fieldset>
          )
        })}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">Recommendation</legend>
          <div className="flex gap-1.5" role="group" aria-label="Recommendation">
            {SCORECARD_RECOMMENDATIONS.map((option) => {
              const selected = draft.recommendation === option.value
              return (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={selected ? 'default' : 'outline'}
                  aria-pressed={selected}
                  onClick={() => setDraft((d) => ({ ...d, recommendation: option.value }))}
                >
                  {option.label}
                </Button>
              )
            })}
          </div>
        </fieldset>

        <FormField label="Summary" htmlFor="scorecard-summary">
          <Textarea
            id="scorecard-summary"
            rows={3}
            value={draft.summary}
            onChange={(e) => setDraft((d) => ({ ...d, summary: e.target.value }))}
          />
        </FormField>
      </div>
      <ArtifactCanvasInlineError>{error}</ArtifactCanvasInlineError>
      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={onSave}
            isDirty
            isSaving={submit.isPending}
            saveLabel={previous ? 'Update scorecard' : 'Submit scorecard'}
            savingLabel="Submitting…"
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
